import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import { seoConfig } from "../utils/seoConfig";
import { useAudio } from "../context/AudioContext";

const films = [
  {
    id: 1,
    title: "La Folle Histoire de Max & Léon",
    year: 2016,
    director: "Jonathan Barré",
    description:
      "Ma première Bande Originale pour le cinéma. Un projet marquant où j'ai pu mélanger énergie comique et ampleur cinématographique.",
    image: "/images/max-leon.jpg",
    video: "/videos/IMG_7195.mp4",
    caption:
      'Enregistrement avec orchestre au "Dada Studios" à Bruxelles, dirigé par Gisèle Gérard-Tolini.',
    trailerUrl: "https://www.youtube.com/embed/rDWeALr9g3o",
    musicAnchor: "max-et-leon",
  },
  {
    id: 2,
    title: "Les Vedettes",
    year: 2022,
    director: "Jonathan Barré",
    description:
      "Une BO surf rock rétro portée par la guitare, la basse et la batterie. Simplement Dan impose son style.",
    image: "/images/les-vedettes.jpg",
    trailerUrl: "https://www.youtube.com/embed/fUILGxPu46o",
    musicAnchor: "les-vedettes",
  },
  {
    id: 3,
    title: "Bonne Conduite",
    year: 2023,
    director: "Jonathan Barré",
    description:
      "Une partition électronique aux atmosphères contrastées, où tension et second degré se répondent avec le même sérieux.",
    image: "/images/bonne-conduite.jpg",
    trailerUrl: "https://www.youtube.com/embed/sRLz3-gcjgg",
    musicAnchor: "bonne-conduite",
  },
];

const Cinema = () => {
  const [activeTrailer, setActiveTrailer] = useState(null);
  const navigate = useNavigate();

  /* 🎧 AUDIO CONTEXT */
  const { pauseMini, setActivePlayer, currentPlayer } = useAudio();

  /* 🎬 refs vidéos locales */
  const videoRefs = useRef({});

  /* 🎬 ref trailer YouTube */
  const trailerIframeRef = useRef(null);

  const openTrailer = (url) => {
    pauseMini();
    setActivePlayer("external");
    setActiveTrailer(url);
  };

  const closeTrailer = () => setActiveTrailer(null);

  const handleMusicLink = (anchor) => {
    pauseMini();
    setActivePlayer("navigation");
    navigate("/musique", { state: { scrollTo: anchor } });
  };

  /* 🔁 MINI-LECTEUR → COUPE TOUTES LES VIDÉOS */
  useEffect(() => {
    if (currentPlayer === "navigation") {
      Object.values(videoRefs.current).forEach((video) => {
        try {
          video?.pause();
        } catch {}
      });

      trailerIframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({
          event: "command",
          func: "pauseVideo",
          args: [],
        }),
        "*"
      );
    }
  }, [currentPlayer]);

  return (
    <>
      <SEO
        title={seoConfig.cinema.title}
        description={seoConfig.cinema.description}
        keywords={seoConfig.cinema.keywords}
        url={seoConfig.cinema.url}
      />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="min-h-screen bg-black text-white relative overflow-hidden"
      >
        {/* HEADER */}
        <div className="relative py-0 md:py-40 overflow-hidden">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              background:
                "radial-gradient(circle at 30% 50%, rgba(255, 0, 0, 0.3) 0%, transparent 70%)",
            }}
          />

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="md:hidden h-44" />

            <h1
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-4"
              style={{ color: "#CC5555" }}
            >
              CINÉMA
            </h1>

            <div
              className="h-1 w-32 md:w-40 mb-6"
              style={{ background: "linear-gradient(to right, #CC5555, transparent)" }}
            />

            <p className="text-lg md:text-xl text-gray-300">Compositeur</p>

            <p className="text-sm md:text-base text-gray-400 mt-4 animate-pulse">
              Défiler pour découvrir les films ↓
            </p>
          </div>
        </div>

        {/* TIMELINE */}
        <div
          className="
            relative max-w-6xl mx-auto px-4 sm:px-6 lg:-mt-28
            before:content-[''] before:absolute before:left-1/2 before:top-0 before:bottom-0
            before:w-1 before:bg-gradient-to-b before:from-red-700 before:to-transparent
            before:opacity-20 before:hidden md:before:block
          "
        >
          {films.map((film, index) => (
            <div key={film.id}>
              <motion.div
                className={`mb-10 md:mb-20 flex flex-col ${
                  index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                } items-center`}
                initial={{ opacity: 0, y: 80 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                {/* IMAGE / VIDÉO */}
                <div className="w-full md:w-1/2 p-4 md:p-6">
                  {film.video ? (
                    <figure className="w-full">
                      <motion.img
                        src={film.image}
                        alt={film.title}
                        className="rounded-2xl shadow-[0_0_40px_rgba(204,85,85,0.4)] w-full mb-4 md:mb-6 cursor-pointer"
                        whileHover={{ scale: 1.03 }}
                        onClick={() => openTrailer(film.trailerUrl)}
                      />

                      <motion.video
                        ref={(el) => {
                          if (el) videoRefs.current[film.id] = el;
                        }}
                        src={film.video}
                        controls
                        className="w-full rounded-2xl shadow-[0_0_40px_rgba(204,85,85,0.4)]"
                        onPlay={() => {
                          pauseMini();
                          setActivePlayer("external");
                        }}
                      />

                      {film.caption && (
                        <figcaption className="text-sm md:text-base text-gray-400 mt-2 italic">
                          {film.caption}
                        </figcaption>
                      )}
                    </figure>
                  ) : (
                    <motion.img
                      src={film.image}
                      alt={film.title}
                      className="rounded-2xl shadow-[0_0_40px_rgba(204,85,85,0.4)] w-full cursor-pointer"
                      whileHover={{ scale: 1.03 }}
                      onClick={() => openTrailer(film.trailerUrl)}
                    />
                  )}
                </div>

                {/* TEXTE */}
                <div
                  className={`
                    w-full md:w-1/2 p-4 md:p-6 text-left
                    ${index % 2 === 0 ? "md:pl-12" : "md:pr-12"}
                    ${film.id === 1 ? "lg:-mt-[330px]" : ""}
                  `}
                >
                  <h2 className="text-2xl md:text-3xl font-extrabold text-red-500 mb-2">
                    {film.title}
                  </h2>

                  <p className="text-gray-400 text-xs md:text-sm mb-2">
                    {film.director} · {film.year}
                  </p>

                  <p className="text-gray-300 text-sm md:text-base mb-4">
                    {film.description}
                  </p>

                  <button
                    onClick={() => handleMusicLink(film.musicAnchor)}
                    className="inline-flex items-center gap-2 px-6 py-3 border-2 rounded-lg hover:bg-red-500/10 hover:scale-105 transition-all"
                    style={{ borderColor: "#CC5555", color: "#CC5555" }}
                  >
                    Écouter la BO
                  </button>
                </div>
              </motion.div>

              {index < films.length - 1 && (
                <div className="md:hidden mx-auto w-2/3 h-px bg-white/10 mb-10"></div>
              )}
            </div>
          ))}
        </div>

        {/* MODAL TRAILER */}
        <AnimatePresence>
          {activeTrailer && (
            <motion.div
              className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeTrailer}
            >
              <motion.div
                className="relative w-full max-w-5xl aspect-video"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.3 }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="absolute -top-12 right-0 text-white text-4xl hover:text-red-500"
                  onClick={closeTrailer}
                >
                  ×
                </button>

                <iframe
                  ref={trailerIframeRef}
                  src={`${activeTrailer}?enablejsapi=1&autoplay=0`}
                  className="w-full h-full rounded-2xl shadow-2xl"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Bande-annonce"
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
};

export default Cinema;
